import styled from "@emotion/styled";
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatNumber, i18n, persistor, ProductTypeCodeEnum, setProgressBarPercentage, useAppDispatch, usePaymentSlip } from 'src/common';
import { processQueryResult } from 'src/common/api/apollo';
import { useGetGeneralTransferPaymentDetailsQuery } from 'src/common/api/apollo/supplementary';
import { BarCode, Button, Card, Flex, Loader, Text } from 'src/components';
import { colors } from 'src/theme';

const CardContainerUp = styled(Flex)({
  padding: '8px 16px',
  backgroundColor: colors.primary1,
  borderRadius: '12px 12px 0 0'
})

const CardContainerDown = styled(Flex)({
  padding: '8px 16px',
  borderRadius: '12px'
})

const FlexCard = styled(Flex)({
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: colors.background6,
  width: '100%'
})

const FlexStyle = styled(Flex)({
  width: '100%'
})

const CheckoutPaymentSentPage = () => {
  const dispatch = useAppDispatch();

  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const { loading: loadingPaymentOptions, data: dataPaymentOptions } = useGetGeneralTransferPaymentDetailsQuery({
    // Treba poslati 12 i 1
    variables: { id: offerId, input: { installments: 1, type: ProductTypeCodeEnum.DOPUNSKO } },
    fetchPolicy: 'cache-first',
    onCompleted: (result: any) => {
      processQueryResult(result);
    },
  });

  const data = dataPaymentOptions?.getGeneralTransferPaymentDetails;

  useEffect(() => {
    dispatch(setProgressBarPercentage(100));
    persistor.purge();
  }, []);

  const { downloadSlip, loading, PaymentSlip } = usePaymentSlip(offerId, 'oneTime');

  if (loading || loadingPaymentOptions || !data) return <Loader page />;

  // Definirajte podatke za prikaz u array-u
  const paymentDetails = [
    {
      label: i18n.t('pages.checkoutPaymentSentPage.payer'),
      value: `${data.customerFirstName} ${data.customerLastName}`,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.receiver'),
      value: (
        <Flex vertical align='flex-end'>
          <Text type="small" color={colors.text1}>{data.recipientName}</Text>
          <Text type="small" color={colors.text1}>{data.recipientAddress}</Text>
          <Text type="small" color={colors.text1}>{`${data.recipientZipCode} ${data.recipientCity}`}</Text>
        </Flex>
      ),
      align: 'flex-start' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.amount'),
      value: data.price ? `${formatNumber(data.price)}€` : '',
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.referenceNumber'),
      value: `${data.referencePrefix}${data.referenceNumberType === 'oib' ? data.referenceNumber : ''}${data.referenceSuffix}`,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.model'),
      value: data.model,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.description'),
      value: data.description,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.iban'),
      value: data.recipientAccountNumber,
      align: 'center' as const
    }
  ];

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={8}>
        <Text type="xxxLarge" bold>{i18n.t('pages.checkoutPaymentSentPage.payment')}</Text>
        <Text type="base">{i18n.t('pages.checkoutPaymentSentPage.paymentSubtitle')}</Text>
      </Flex>
      <Card padding='0px' radius={12}>
        <CardContainerUp justify='space-between'>
          <Text type="base" color={colors.white}>{i18n.t('pages.checkoutPaymentSentPage.toPay')}</Text>
          <Text type="base" color={colors.white} bold>{data.price ? `${formatNumber(data.price)}€` : ''}</Text>
        </CardContainerUp>
        <CardContainerDown vertical gap={24}>
          <Flex vertical gap={8} align='center'>
            <Text type="small" color={colors.text1}>{i18n.t('pages.checkoutPaymentSentPage.barcodePayment')}</Text>
            <BarCode price={formatNumber(data.price || 0)} userFirstName={data.customerFirstName || ''} userLastName={data.customerLastName || ''} userAddress={data.customerAddress || ''} userCity={data.customerCity || ''} oib={data.customerOib || ''} recipient={data.recipientName || ''} recipientAddress={data.recipientAddress || ''} recipientCity={data.recipientCity || ''} model={data.model || ''} iban={data.recipientAccountNumber || ''} description={data.description || ''} hidden={false} />
          </Flex>
          <Flex vertical gap={8} align='center'>
            <Text type="small" color={colors.text1}>{i18n.t('pages.checkoutPaymentSentPage.or')}</Text>
          </Flex>
          <Flex vertical gap={8} align='center'>
            <PaymentSlip />
            <Text type='small'>{i18n.t('pages.checkoutPaymentSentPage.downloadSlip')}</Text>
            <FlexStyle vertical gap={2} align='center'>
              {paymentDetails.map((detail, index) => (
                <FlexCard key={index} gap={16} align={detail.align} justify='space-between'>
                  <Text type="tiny" color={colors.text4}>{detail.label}</Text>
                  {typeof detail.value === 'string' ? (
                    <Text type="small" color={colors.text1}>{detail.value}</Text>
                  ) : (
                    detail.value
                  )}
                </FlexCard>
              ))}
            </FlexStyle>
            <Button btnType='outlinePrimary' size='large' onClick={() => downloadSlip()} style={{ width: '100%' }}>{i18n.t('pages.checkoutPaymentSentPage.downloadSlipButton')}</Button>
          </Flex>

        </CardContainerDown>
      </Card>
    </Flex>

  );
}

export default CheckoutPaymentSentPage;
